import { TextField } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import React, { useEffect, useState } from "react";
import ReactDatePicker from "react-datepicker";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ClipLoader } from "react-spinners";
import Button from "../../../../components/Common/Button";
import { qualificationTypes } from "../../../../db/models/const";
import { qualificationScopes } from "../../../../db/datasets/qualifications";
import { selectUid } from "../../../../slices/auth";
import { updateStudentBgItem } from "../../../../api/common";
import "./style.scss";
import { StudentQualificationInterface } from "../../../../db/models/student/cv";
import { DocTimestamp } from "../../../../db/models/extra";
import { selectUser } from "../../../../slices/profile";
import { getSchoolById } from "../../../../api/school";

interface Props {
	handleGoNext: Function;
}

type FormInterface = {
	studyScope: { id: number; label: string };
	typeId: number;
	begin: DocTimestamp;
	end: DocTimestamp;
};

const StudentOnboardingStudy = ({ handleGoNext }) => {
	const [loading, setLoading] = useState(false);
	const [ongoingFlag, setOngoingFlag] = useState(true);
	const [schoolName, setSchoolName] = useState<string | null>(null);
	const uid = useSelector(selectUid);
	const user = useSelector(selectUser);
	const { t } = useTranslation();

	const { register, handleSubmit, control, errors, reset, getValues, setValue } = useForm({
		defaultValues: {
			studyScope: qualificationScopes[0],
			typeId: undefined,
			begin: null,
			end: null,
			place: "",
		},
	});

	useEffect(() => {
		const schoolId = user?.invitedBySchool;
		if (schoolId) {
			getSchoolById(schoolId).then((school) => {
				if (school?.name) {
					setSchoolName(school.name);
					setValue("place", school.name);
				}
			});
		}
	}, [user]);

	const onSave = async (form: any) => {
		const q: Partial<StudentQualificationInterface> = {
			scopeId: Number(form.studyScope.id),
			typeId: Number(form.typeId),
			label: "",
			begin: form.begin,
			end: form.end || null,
			description: "",
			place: form.place,
		};

		try {
			setLoading(true);
			await updateStudentBgItem(uid, "QUAL", q, true);
			setLoading(false);
			handleGoNext();
		} catch (error) {
			console.error(error);
			setLoading(false);
		}
	};

	const handleOngoingFlagSelect = (ongoingFlagChecked: boolean) => {
		if (ongoingFlagChecked) reset({ ...getValues(), end: null });
		setOngoingFlag(ongoingFlagChecked);
	};

	return (
		<div className="studentOnboardingStudy dark-bg">
			<div className="container">
				<div className="card p-4">
					<h2 className="fs-2 fw-bolder">{t("student_onboarding_study_title")}</h2>
					<p className="mb-4 fs-4">{t("student_onboarding_study_subtitle")}</p>
					<form onSubmit={handleSubmit(onSave)}>
						<div className="element">
							<div className="label">{t("student_onboarding_study_place_label") + "*"}</div>
							<input type="text" name="place" disabled={schoolName != null} ref={register({ required: true })} placeholder={t("student_onboarding_study_place_ph")}></input>
							{errors.place && <p className="error">{t("student_onboarding_study_place_error")}</p>}
						</div>
						<div className="element">
							<div className="label">{t("student_qual_scope_label") + "*"}</div>
							<Controller
								render={({ onChange, ...props }) => (
									<Autocomplete
										{...props}
										options={qualificationScopes}
										getOptionLabel={(option) => t(option.label)}
										renderInput={(params) => <TextField {...params} />}
										onChange={(e, x) => {
											onChange(x);
										}}
									/>
								)}
								name={"studyScope"}
								rules={{ required: true }}
								control={control}
							/>
							{errors.studyScope && <p className="error">{t("student_qual_scope_error")}</p>}
						</div>
						<div className="element">
							<div className="label">{t("student_qual_level_label") + "*"}</div>
							<select name="typeId" ref={register({ required: true })}>
								{[{ id: "", label: t("select_choose_option") }, ...qualificationTypes].map((qt) => (
									<option key={qt.id} value={qt.id}>
										{t(qt.label)}
									</option>
								))}
							</select>
							{errors.typeId && <p className="error">{t("student_qual_level_error")}</p>}
						</div>
						<div className="element">
							<div className="label">{t("student_qual_begin_label") + "*"}</div>
							<Controller control={control} name="begin" rules={{ required: true }} render={({ onChange, onBlur, value }) => <ReactDatePicker onChange={onChange} onBlur={onBlur} selected={value} dateFormat="MM/yyyy" showMonthYearPicker placeholderText="mm/yyyy" popperPlacement="top" />} />
							{errors.begin && <p className="error">{t("student_qual_begin_error")}</p>}
						</div>
						<div className="element">
							<div className="checkbox">
								<input type="checkbox" name="ongoingFlag" checked={ongoingFlag} onChange={(e) => handleOngoingFlagSelect(e.target.checked)} />
								<p>{t("student_qual_ongoingFlag")}</p>
							</div>
						</div>
						<div className={ongoingFlag ? "element element--disabled" : "element"}>
							<div className="label">{t("student_qual_end_label")}</div>
							<Controller control={control} name="end" render={({ onChange, onBlur, value }) => <ReactDatePicker onChange={onChange} onBlur={onBlur} selected={value} dateFormat="MM/yyyy" showMonthYearPicker disabled={ongoingFlag} placeholderText="mm/yyyy" popperPlacement="top" />} />
						</div>
						{!loading && (
							<Button type="submit" filled loading={loading} backgroundColor="#3392E8" borderColor="#3392E8">
								{t("student_onboarding_nextBtn")}
							</Button>
						)}
						{loading && (
							<div className="loader">
								<ClipLoader size={20} color={"#7400b8"} loading={true} />
							</div>
						)}
					</form>
				</div>
			</div>
		</div>
	);
};

export default StudentOnboardingStudy;
